const AI_FILENAME_PATTERN = /_AI\.[a-z0-9]+$/i

function getFilenameFromUrl(source: string): string {
	if (!source) return ''
	const normalizedSource = source.replace(/&amp;/g, '&')

	try {
		const url = new URL(normalizedSource, window.location.origin)

		// Dynamicweb often serves assets via GetImage.ashx?Image=/path/file_AI.jpg
		const imageParam = url.searchParams.get('Image') || url.searchParams.get('image')
		if (imageParam) {
			const imagePath = decodeURIComponent(imageParam)
			return imagePath.split('/').pop() ?? ''
		}

		return url.pathname.split('/').pop() ?? ''
	} catch {
		const [pathWithQuery] = normalizedSource.split('#')
		const [path, queryString] = pathWithQuery.split('?')

		if (queryString) {
			const params = new URLSearchParams(queryString)
			const imageParam = params.get('Image') || params.get('image')
			if (imageParam) {
				const imagePath = decodeURIComponent(imageParam)
				return imagePath.split('/').pop() ?? ''
			}
		}

		return path.split('/').pop() ?? ''
	}
}

function isAiImageFilename(source: string): boolean {
	return AI_FILENAME_PATTERN.test(getFilenameFromUrl(source))
}

function resolveHost(image: HTMLImageElement): HTMLElement | null {
	const explicitHost = image.closest('[data-ai-image-host], .ai-content-image, .product-image-wrapper, .splide__slide')
	if (explicitHost instanceof HTMLElement) return explicitHost

	if (!image.parentElement) return null

	if (image.parentElement.tagName === 'PICTURE' && image.parentElement.parentElement) {
		return image.parentElement.parentElement
	}

	return image.parentElement
}

// Selectors where the AI badge/wrapper should never be injected.
// Mobile navigation is layout-sensitive (flex/aspect-ratio based image grid)
// and the badge has no real value there — a wrapper div breaks the layout.
const AI_EXCLUDED_SELECTOR = '[data-no-ai-badge], .js-mobile-navigation'

function markImage(image: HTMLImageElement): void {
	if (image.closest(AI_EXCLUDED_SELECTOR)) return

	const source = image.currentSrc || image.getAttribute('src') || ''
	const shouldMarkFromFilename = isAiImageFilename(source)

	// Find or create the wrapper
	let wrapper = image.parentElement
	if (!wrapper?.classList.contains('ai-content-wrapper')) {
		wrapper = document.createElement('div')
		wrapper.className = 'ai-content-wrapper'
		image.parentElement?.insertBefore(wrapper, image)
		wrapper.appendChild(image)
	}

	// Find or create the overlay
	let overlay = wrapper.querySelector('.ai-content-overlay') as HTMLElement | null
	if (!overlay) {
		overlay = document.createElement('div')
		overlay.className = 'ai-content-overlay'
		wrapper.appendChild(overlay)
	}

	// Set/remove classes
	overlay.classList.toggle('is-ai-image', shouldMarkFromFilename)
	image.classList.toggle('ai-content-image', shouldMarkFromFilename)

	// Track that we've bound this image
	if (image.dataset.aiContentBound !== '1') {
		image.dataset.aiContentBound = '1'
		image.addEventListener('load', () => markImage(image), { passive: true })
	}
}

function scanForAiContentImages(root: ParentNode = document): void {
	const images = root.querySelectorAll('img')
	images.forEach((image) => markImage(image as HTMLImageElement))
}

export function initAiContentImageMarker(): void {
	const bootstrap = () => {
		scanForAiContentImages(document)

		let frameId = 0
		const scheduleScan = () => {
			if (frameId) return
			frameId = window.requestAnimationFrame(() => {
				frameId = 0
				scanForAiContentImages(document)
			})
		}

		const observer = new MutationObserver(() => {
			scheduleScan()
		})

		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['src', 'srcset']
		})
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', bootstrap, { once: true })
		return
	}

	bootstrap()
}

